import { Link } from 'react-router-dom';
import { 
  Phone, 
  ArrowRight, 
  HeartHandshake,
  Stethoscope,
  Home as HomeIcon,
  Users,
  Building2,
  Gift,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';

const ServiceCard = ({ icon: Icon, title, subtitle, description, includes, gradient }) => (
  <div className="grid lg:grid-cols-2 gap-8 items-start">
    <div>
      <div className="flex items-center gap-4 mb-6">
        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
          <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-[#1e293b]">{title}</h2>
          <p className="text-[#23D3D3] font-medium text-sm">{subtitle}</p>
        </div>
      </div>
      <p className="text-lg text-[#64748b] leading-relaxed">{description}</p>
    </div>
    <div className="bg-gradient-to-br from-[#f8fafb] to-white rounded-2xl p-6 border border-[#e2e8f0]">
      <h3 className="font-semibold text-[#1e293b] mb-4 text-sm uppercase tracking-wider">Das beinhaltet:</h3>
      <ul className="space-y-3">
        {includes.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
            <span className="text-[#64748b]">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export default function Leistungen() {
  const services = [
    {
      icon: HeartHandshake,
      title: 'Grundpflege',
      subtitle: 'Körperbezogene Pflegemaßnahmen',
      description: 'Wir unterstützen Sie liebevoll bei allen Tätigkeiten der täglichen Körperpflege. Unsere qualifizierten Pflegekräfte helfen Ihnen, Ihre Selbstständigkeit zu bewahren.',
      gradient: 'from-[#1A52D8] to-[#23D3D3]',
      includes: ['Körperpflege (Waschen, Duschen, Baden)', 'An- und Auskleiden', 'Hilfe bei der Nahrungsaufnahme', 'Mobilisation und Lagerung', 'Inkontinenzversorgung', 'Prophylaxen (Dekubitus, Thrombose)']
    },
    {
      icon: Stethoscope,
      title: 'Behandlungspflege',
      subtitle: 'Medizinische Versorgung',
      description: 'Unsere examinierten Pflegefachkräfte führen alle ärztlich verordneten medizinischen Maßnahmen professionell und zuverlässig durch.',
      gradient: 'from-[#23D3D3] to-[#98E72B]',
      includes: ['Medikamentengabe und -überwachung', 'Wundversorgung', 'Injektionen (z.B. Insulin)', 'Blutdruck- und Blutzuckermessung', 'Kompressionsverbände', 'Versorgung von Kathetern']
    },
    {
      icon: HomeIcon,
      title: 'Hauswirtschaft',
      subtitle: 'Hilfe im Alltag',
      description: 'Damit Sie sich in Ihrem Zuhause wohlfühlen, übernehmen wir gerne hauswirtschaftliche Aufgaben und unterstützen Sie im Alltag.',
      gradient: 'from-[#1A52D8] to-[#1545b8]',
      includes: ['Reinigung der Wohnung', 'Wäschepflege', 'Einkäufe und Besorgungen', 'Zubereitung von Mahlzeiten', 'Begleitung zu Arztbesuchen', 'Pflanzen- und Tierpflege']
    },
    {
      icon: Users,
      title: 'Pflegeberatung',
      subtitle: 'Kompetente Beratung',
      description: 'Wir stehen Ihnen und Ihren Angehörigen beratend zur Seite und helfen Ihnen, die beste Pflege zu organisieren.',
      gradient: 'from-[#23D3D3] to-[#1A52D8]',
      includes: ['Beratung zum Pflegegrad', 'Information über Leistungsansprüche', 'Entlastungsmöglichkeiten', 'Hilfsmittelberatung', 'Wohnraumanpassung', 'Vorsorgevollmacht']
    },
    {
      icon: Building2,
      title: 'Nach Krankenhausaufenthalt',
      subtitle: 'Übergangsversorgung',
      description: 'Nach einem Krankenhausaufenthalt begleiten wir Sie sicher zurück in Ihren Alltag und sorgen für nahtlose Versorgung.',
      gradient: 'from-[#1A52D8] to-[#23D3D3]',
      includes: ['Schnelle Übernahme der Pflege', 'Koordination mit Krankenhaus', 'Medikamentenmanagement', 'Wundversorgung', 'Mobilisierung', 'Angehörigenberatung']
    },
    {
      icon: Gift,
      title: 'Entlastungsleistungen',
      subtitle: 'Zusätzliche Betreuung',
      description: 'Nutzen Sie Ihren Entlastungsbetrag von 125€ monatlich für zusätzliche Betreuungsleistungen.',
      gradient: 'from-[#98E72B] to-[#23D3D3]',
      includes: ['Begleitete Spaziergänge', 'Gesellschaft und Gespräche', 'Vorlesen und Spielen', 'Gedächtnistraining', 'Begleitung bei Aktivitäten', 'Entlastung pflegender Angehöriger']
    }
  ];

  return (
    <main id="main-content" data-testid="leistungen-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafb] via-white to-[#e8f8f8]" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#1A52D8]/5 rounded-full blur-3xl" />
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A52D8]/5 rounded-full mb-6">
              <HeartHandshake className="w-4 h-4 text-[#1A52D8]" />
              <span className="text-sm font-medium text-[#1A52D8]">Unsere Leistungen</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1e293b] leading-tight mb-6">
              Professionelle Pflege – individuell für Sie
            </h1>
            <p className="text-lg text-[#64748b] leading-relaxed">
              Von der Grundpflege bis zur medizinischen Versorgung: Umfassende 
              Pflegeleistungen in Ihrer vertrauten Umgebung in Itzehoe.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 bg-white" data-testid="services-list">
        <div className="container-custom">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div key={service.title} data-testid={`service-detail-${index}`}>
                <ServiceCard {...service} />
                {index < services.length - 1 && (
                  <div className="mt-20 h-px bg-gradient-to-r from-transparent via-[#e2e8f0] to-transparent" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Info Box */}
      <section className="py-16 bg-[#f8fafb]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl p-8 border border-[#e2e8f0] shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#23D3D3]/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-[#23D3D3]" />
              </div>
              <div>
                <h3 className="font-bold text-[#1e293b] text-lg mb-2">Kostenübernahme</h3>
                <p className="text-[#64748b] leading-relaxed">
                  Viele unserer Leistungen werden von der Pflegekasse oder Krankenkasse übernommen. 
                  Wir beraten Sie kostenlos zu Ihren individuellen Ansprüchen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-testid="leistungen-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A52D8] via-[#1A52D8] to-[#23D3D3]" />
        
        <div className="container-custom text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Welche Pflege brauchen Sie?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-xl mx-auto">
            Wir erstellen gemeinsam einen individuellen Pflegeplan.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+4915146173685" data-testid="leistungen-phone-btn">
              <Button className="bg-white text-[#1A52D8] hover:bg-white/90 h-14 px-10 rounded-full text-lg font-semibold shadow-xl">
                <Phone className="w-5 h-5" />
                Jetzt Anrufen
              </Button>
            </a>
            <Link to="/kontakt" data-testid="leistungen-contact-btn">
              <Button className="bg-transparent text-white border-2 border-white/30 hover:border-white hover:bg-white/10 h-14 px-10 rounded-full text-lg font-semibold">
                Beratung anfordern
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
